import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import { Button } from 'components/Toolkit/Button/Button';
import {
  Dropdown,
  SButtonDropdown,
} from 'components/Toolkit/Dropdown/Dropdown';
import { Checkbox } from 'components/Toolkit/Inputs/Checkbox';
import { media } from 'helpers/breakpoints';
import {
  InputButton,
  SButtonInput,
} from 'components/Toolkit/InputButton/InputButton';
import { BottomSheet } from 'components/Toolkit/BottomSheet/BottomSheet';
import { Label } from 'components/Toolkit/Inputs/Label';
import { StickyFooter } from 'components/Toolkit/StickyFooter/StickyFooter';
import { Columns } from 'components/Toolkit/Columns/Columns';
import { Spacer } from 'components/SearchPage/styles';

import { useOnUpdateOnly } from 'hooks/UseOnUpdateOnly';

export type ListMultiSelectOption = {
  value: string;
  label: string;
};

export interface IListMultiSelectProps {
  name: string;
  placeholder: string;
  label?: string;
  options: ListMultiSelectOption[];
  selectedOptions?: ListMultiSelectOption[];
  onSelectOption?: (option: ListMultiSelectOption, value: boolean) => void;
  onClear?: () => void;
  onDone?: () => void;
  isLoading?: boolean;
  isDisabled?: boolean;
  bottomSheetClassName?: string;
  onClose?: () => void;
}

const List = styled.ul`
  ${({ theme }) => theme.fontSize.M14};
  background-color: ${({ theme }) => theme.colors.GREY_LIGHTER};
  padding: 0 0 1px 0;
`;

const SButtonInputDisabled = css`
  ${SButtonInput},
  ${SButtonDropdown} {
    background-color: ${({ theme }) => theme.colors.GREY_LIGHTER};
    color: ${({ theme }) => theme.colors.GREY_LIGHT};
    border-color: ${({ theme }) => theme.colors.GREY_LIGHT};
  }
`;

const Mobile = styled.div<{ isDisabled?: boolean }>`
  display: block;
  ${({ isDisabled }) => isDisabled && SButtonInputDisabled};
  ${media.large} {
    display: none;
  }
`;

const Desktop = styled.div<{ isDisabled?: boolean }>`
  display: none;
  ${({ isDisabled }) => isDisabled && SButtonInputDisabled};
  ${media.large} {
    display: block;
  }
`;

const A11yButton = styled.button<{ isDefault?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => `${theme.spacing.S8} ${theme.spacing.M16}`};
  background-color: ${({ theme }) => theme.colors.WHITE};
  color: ${({ theme }) => theme.colors.GREY_DARKER};
  width: 100%;
  height: 40px;
  text-align: left;
  margin: ${({ isDefault }) => (isDefault ? `0 0 1px` : `1px 0`)};
  cursor: pointer;

  &:last-child {
    margin-bottom: 0;
  }

  &:hover {
    background-color: ${({ theme }) => theme.colors.OFFWHITE};
  }
`;

export const SListMultiSelect = styled.div``;

const SCheckbox = styled(Checkbox)`
  pointer-events: none;
  min-height: 0;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ListHeadingItem = styled.span`
  ${({ theme }) => theme.fontSize.S12};
  padding: ${({ theme }) => `0 ${theme.spacing.M16}`};
  color: ${({ theme }) => theme.colors.GREY};
  margin: -1px 0;
`;

function ListMultiSelect(props: IListMultiSelectProps) {
  const {
    label,
    placeholder,
    options,
    selectedOptions,
    onSelectOption,
    onClear,
    onDone,
    isLoading,
    isDisabled,
    bottomSheetClassName,
    onClose,
  } = props;
  const [openBottomSheet, setOpenBottomSheet] = useState<boolean>(false);
  const [isExpandedDropdown, setExpandedDropdown] = useState<boolean>(false);

  const getOptions = () =>
    options.map((option, index) => {
      const isSelected =
        selectedOptions &&
        selectedOptions.find(
          (selectedOption) => selectedOption.value === option.value,
        );
      const isHeadingItem = index > 0 && option.value === '';
      return (
        <li key={index}>
          {isHeadingItem ? (
            <ListHeadingItem key={index}>{option.label}</ListHeadingItem>
          ) : (
            <A11yButton
              isDefault={false}
              onClick={(e) => {
                e.preventDefault();
                onSelectOption && onSelectOption(option, !!isSelected);
              }}
            >
              {option.label}
              <SCheckbox
                id={option.value}
                name={option.value}
                value={option.value}
                checked={!!isSelected}
                willUseSubText={false}
              />
            </A11yButton>
          )}
        </li>
      );
    });

  const handleOnDone = () => {
    setOpenBottomSheet(false);
    setExpandedDropdown(false);
    onDone && onDone();
  };

  const handleOnClear = () => {
    onClear && onClear();
  };

  useOnUpdateOnly(() => {
    if (!openBottomSheet) {
      onClose && onClose();
    }
  }, [openBottomSheet]);

  useOnUpdateOnly(() => {
    if (!isExpandedDropdown) {
      onClose && onClose();
    }
  }, [isExpandedDropdown]);

  return (
    <SListMultiSelect>
      {label && <Label variant="COMFORT">{label}</Label>}
      <Mobile isDisabled={isDisabled}>
        <InputButton
          isActive={openBottomSheet}
          value={placeholder}
          onClick={() => !isLoading && setOpenBottomSheet(true)}
          placeholder={placeholder}
          variant="DEFAULT"
          isLoading={isLoading}
          isDisabled={isDisabled}
        />
        <BottomSheet
          headerClassName={bottomSheetClassName}
          buttonA={
            <Button ofType="SECONDARY" onClick={handleOnClear}>
              Clear
            </Button>
          }
          buttonB={
            <Button ofType="PRIMARY" onClick={handleOnDone}>
              Done
            </Button>
          }
          isOpen={openBottomSheet}
          askToClose={() => setOpenBottomSheet(false)}
          headerText={placeholder}
          onApply={() => setOpenBottomSheet(false)}
        >
          <List>{getOptions()}</List>
        </BottomSheet>
      </Mobile>
      <Desktop isDisabled={isDisabled}>
        <Dropdown
          placeholder={placeholder}
          expanded={isExpandedDropdown}
          setExpanded={(isExpanded: boolean) =>
            !isLoading && setExpandedDropdown(isExpanded)
          }
          displayedValue={placeholder}
          isLoading={isLoading}
          isDisabled={isDisabled}
        >
          <List>{getOptions()}</List>
          <StickyFooter position="sticky">
            <Columns>
              <Button ofType="SECONDARY" onClick={handleOnClear}>
                Clear
              </Button>
              <Button onClick={handleOnDone}>Done</Button>
            </Columns>
          </StickyFooter>
        </Dropdown>
      </Desktop>
      <Spacer />
    </SListMultiSelect>
  );
}

export { ListMultiSelect };
